import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import { useStore } from 'zustand';
import { useOverlayContext } from 'seagull';
import { useIoContext } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useLocaleKeys } from '~/i18n';
import { useTasksDrawerStoreApi } from '../../tasksDrawerStore';
import { buildCompletionToastPayload, GetCompletionToastTID } from '../utils/completionToast.utils';
import { toastContent } from '../utils/toastContent';
import { emitPersistent, runAction } from './useCompletionToast.utils';
export const COMPLETION_TOAST_DEBOUNCE_MS = 3000;
export { GetCompletionToastTID };
export function useCompletionToast() {
    const { t: tasksDrawerTexts } = useLocaleKeys('tasksDrawer');
    const { showSuccessToast, showWarningToast, showPersistentToast } = useOverlayContext();
    const { browser: { openNewTab } } = useIoContext();
    const { retryAgreementPdf } = useDownloadAgreementPdf();
    const retryAgreementPdfRef = useRef(retryAgreementPdf);
    retryAgreementPdfRef.current = retryAgreementPdf;
    const drawerStore = useTasksDrawerStoreApi();
    const openDrawer = useStore(drawerStore, (s)=>s.openDrawer);
    const bufferRef = useRef([]);
    const activeDismissersRef = useRef(new Set());
    const trackDismiss = useCallback((dismiss)=>{
        activeDismissersRef.current.add(dismiss);
        return ()=>activeDismissersRef.current.delete(dismiss);
    }, []);
    const emit = useCallback(()=>{
        const transitions = bufferRef.current;
        bufferRef.current = [];
        const { isDrawerOpen, isTasksDrawerAvailable } = drawerStore.getState();
        if (isDrawerOpen) return;
        const payload = buildCompletionToastPayload(transitions, tasksDrawerTexts);
        if (payload == null) return;
        const runtime = {
            openNewTab,
            openDrawer,
            retry: (relationshipId, options)=>retryAgreementPdfRef.current(relationshipId, options),
            isDrawerAvailable: isTasksDrawerAvailable
        };
        if (!isTasksDrawerAvailable) {
            emitPersistent(payload, tasksDrawerTexts.readyToast.close(), runtime, showPersistentToast, trackDismiss);
            return;
        }
        const content = toastContent(payload.message, payload.action.label, payload.action.biName, ()=>runAction(payload.action, runtime));
        if (payload.kind === 'success') showSuccessToast(content, payload.testId);
        else showWarningToast(content, payload.testId);
    }, [
        drawerStore,
        openDrawer,
        trackDismiss
    ]);
    const emitRef = useRef(emit);
    useEffect(()=>{
        emitRef.current = emit;
    }, [
        emit
    ]);
    const debouncedEmit = useMemo(()=>debounce(()=>emitRef.current(), COMPLETION_TOAST_DEBOUNCE_MS, {
            leading: false,
            trailing: true
        }), []);
    const reportCompletion = useCallback((transition)=>{
        if (drawerStore.getState().isDrawerOpen) return;
        bufferRef.current.push(transition);
        const sameKindStillPending = Array.from(drawerStore.getState().awaitedJobs.values()).some((job)=>job.kind === transition.kind);
        if (sameKindStillPending) {
            debouncedEmit();
            return;
        }
        debouncedEmit.cancel();
        emitRef.current();
    }, [
        drawerStore,
        debouncedEmit
    ]);
    useEffect(()=>()=>{
            debouncedEmit.cancel();
            const dismissers = Array.from(activeDismissersRef.current);
            activeDismissersRef.current.clear();
            dismissers.forEach((d)=>d());
        }, [
        debouncedEmit
    ]);
    return reportCompletion;
}
